import React, { useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import {
  type AnalyticalFieldMappingV2,
  type AnalyticalFieldValueMappingV2,
} from 'src/core/modules/bookkeep';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { CustomFieldEditValuesModal } from '../../components/CustomFieldEditValuesModal/CustomFieldEditValuesModal';
import { useUpdateCustomFieldValuesMutation } from '../../hooks/useUpdateCustomFieldValuesMutation';
import { type CustomField } from '../../models/customField';
import { convertValues } from '../../utils/convertValues';
import { removeDuplicates } from '../../utils/removeDuplicates';

type Props = {
  isOpen: boolean;
  customField: CustomField;
  onClose(): void;
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  handleValueMapping: (arguments_: {
    value?: AnalyticalFieldValueMappingV2;
    internalId: string;
  }) => void;
  isMapSyncEnabled: boolean;
  syncMode?: SyncMode;
};

export const CustomFieldEditValuesModalContainer = ({
  isOpen,
  customField,
  onClose,
  fieldMapping,
  isMapSyncEnabled,
  syncMode,
  handleValueMapping,
}: Props) => {
  const [updateCustomFieldValues] = useUpdateCustomFieldValuesMutation(
    customField.id,
  );
  const { t } = useTranslation('global');
  const { successNotif, dangerNotif } = useNotifications();

  const [valueMapping, setValueMapping] =
    useState<AnalyticalFieldValueMappingV2>();

  const handleSubmit = async (values: string) => {
    const valuesToCreate = removeDuplicates(convertValues(values));

    try {
      const result = await updateCustomFieldValues({
        values: valuesToCreate,
      });
      successNotif(t('customFields.edition.successRequest'));

      if (valuesToCreate.length === 1) {
        handleValueMapping({
          value: valueMapping,
          internalId: result.addedCustomFieldValues[0].id,
        });
        setValueMapping(undefined);
      }

      onClose();
      track(AnalyticEventName.CUSTOM_FIELDS_FORM_POST_VALUES, {
        method: 'edition',
        numberOfCharacters: values.length,
        numberOfValues: values.split('\n').length,
      });
    } catch {
      dangerNotif(t('customFields.edition.errorRequest'));
    }
  };

  return (
    <CustomFieldEditValuesModal
      isOpen={isOpen}
      name={customField.name}
      onClose={onClose}
      customField={customField}
      onSubmit={handleSubmit}
      fieldMapping={fieldMapping}
      isMapSyncEnabled={isMapSyncEnabled}
      syncMode={syncMode}
      valueMapping={valueMapping}
      setValueMapping={setValueMapping}
    />
  );
};
